/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ChoiceGroup } from '@fluentui/react/lib/ChoiceGroup';
import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { globalMessages } from 'src/core/utils/globalMessages';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { QnaSourceChitchatTypes } from 'src/qnaMaker/modules/utils/chitchatTypeToFileUrl';

export type QnaAddChitchatSourceModalProps = BasicModalProps<QnaSourceChitchatTypes>;

const messages = defineMessages({
    modalTitle: {
        id: 'qnaMaker.qnaAddChitchatSourceModal.modalTitle',
        defaultMessage: 'Add chit chat'
    },
    modalMainDescription: {
        id: 'qnaMaker.qnaAddChitchatSourceModal.modalMainDescription',
        defaultMessage: 'When you select a chit chat personality, we add a curated list of question and answer pairs to the knowledge base.'
    },
    modalSecondaryDescription: {
        id: 'qnaMaker.qnaAddChitchatSourceModal.modalSecondaryDescription',
        defaultMessage:
            'Chit chat is currently available in the following languages: Chinese Simplified, English, French, German, Italian, Japanese, Korean, Portuguese, and Spanish.'
    },
    addChitchat: {
        id: 'qnaMaker.qnaAddChitchatSourceModal.addChitchat',
        defaultMessage: 'Add chit chat'
    },
    professional: {
        id: 'qnaMaker.qnaAddChitchatSourceModal.professional',
        defaultMessage: 'Professional'
    },
    friendly: {
        id: 'qnaMaker.qnaAddChitchatSourceModal.friendly',
        defaultMessage: 'Friendly'
    },
    witty: {
        id: 'qnaMaker.qnaAddChitchatSourceModal.witty',
        defaultMessage: 'Witty'
    },
    caring: {
        id: 'qnaMaker.qnaAddChitchatSourceModal.caring',
        defaultMessage: 'Caring'
    },
    enthusiastic: {
        id: 'qnaMaker.qnaAddChitchatSourceModal.enthusiastic',
        defaultMessage: 'Enthusiastic'
    }
});

export const QnaAddChitchatSourceModal = withModalMounter((props: QnaAddChitchatSourceModalProps) => {
    const { onSubmit } = props;
    const { localize } = useLocalization();
    const [chitchatType, setChitchatType] = React.useState<QnaSourceChitchatTypes>('professional');

    return (
        <BasicModal
            valid
            {...props}
            minWidth={650}
            onSubmit={() => onSubmit(chitchatType)}
            doneButtonText={localize(messages.addChitchat)}
            dialogContentProps={{ title: localize(messages.modalTitle) }}>
            <Stack styles={{ root: { minHeight: 250 } }} tokens={{ childrenGap: 18 }} verticalFill>
                <Stack tokens={{ childrenGap: 18 }}>
                    <LuisText>
                        {localize(messages.modalMainDescription)}
                        &nbsp;
                        <ExternalLink href="https://aka.ms/qna-chitchat">{localize(globalMessages.learnMore)}</ExternalLink>&nbsp;
                    </LuisText>
                    <LuisText>{localize(messages.modalSecondaryDescription)}</LuisText>
                </Stack>

                <ChoiceGroup
                    styles={{ root: { marginLeft: '24px' } }}
                    selectedKey={chitchatType}
                    options={[
                        {
                            key: 'professional',
                            text: localize(messages.professional)
                        },
                        {
                            key: 'friendly',
                            text: localize(messages.friendly)
                        },
                        {
                            key: 'witty',
                            text: localize(messages.witty)
                        },
                        {
                            key: 'caring',
                            text: localize(messages.caring)
                        },
                        {
                            key: 'enthusiastic',
                            text: localize(messages.enthusiastic)
                        }
                    ]}
                    onChange={(_, o) => setChitchatType(o.key as QnaSourceChitchatTypes)}
                />
            </Stack>
        </BasicModal>
    );
});
